import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface Project {
  title: string;
  industry: string;
  category: string;
  description: string;
  result: string;
  technologies: string[];
  challenge: string;
  solution: string;
  fullDescription: string;
  image?: string;
}
interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ProjectModal({
  project,
  isOpen,
  onClose
}: ProjectModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-139">
      {isOpen && project && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-140">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-141" />

          {/* Modal Content */}
          <motion.div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#09090B] border border-white/10 rounded-2xl shadow-2xl" initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-142">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Schließen" data-id="element-143">
              <XIcon className="w-5 h-5 text-white" data-id="element-144" />
            </button>

            {/* Image */}
            <div className="aspect-[16/9] w-full overflow-hidden rounded-t-2xl" data-id="element-145">
              {project.image ? <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-146" /> : <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/10" data-id="element-147" />}
            </div>

            {/* Content */}
            <div className="p-8 md:p-12" data-id="element-148">
              {/* Tags */}
              <div className="flex items-center gap-3 mb-4" data-id="element-149">
                <span className="text-xs font-medium uppercase tracking-wider text-white/40" data-id="element-150">
                  {project.industry}
                </span>
                <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-151" />
                <span className="text-xs font-medium text-[#E8781A]" data-id="element-152">
                  {project.category}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white" data-id="element-153">
                {project.title}
              </h2>

              {/* Description */}
              <p className="mt-4 text-lg text-white/60 leading-relaxed" data-id="element-154">
                {project.fullDescription}
              </p>

              {/* Result */}
              <div className="mt-8 p-6 bg-white/5 rounded-xl border border-white/10" data-id="element-155">
                <span className="text-sm font-medium text-white/40 uppercase tracking-wider" data-id="element-156">
                  Ergebnisse
                </span>
                <p className="mt-2 text-2xl font-bold text-white" data-id="element-157">
                  {project.result}
                </p>
              </div>

              {/* Technologies */}
              <div className="mt-8" data-id="element-158">
                <span className="text-sm font-medium text-white/40 uppercase tracking-wider" data-id="element-159">
                  Technologien
                </span>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-160">
                  {project.technologies.map(tech => <span key={tech} className="px-3 py-1.5 bg-white/5 border border-white/10 text-sm font-medium text-white/80 rounded-lg" data-id="element-161">
                      {tech}
                    </span>)}
                </div>
              </div>

              {/* Challenge & Solution */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-162">
                <div data-id="element-163">
                  <span className="text-sm font-medium text-white/40 uppercase tracking-wider" data-id="element-164">
                    Herausforderung
                  </span>
                  <p className="mt-3 text-white/60 leading-relaxed" data-id="element-165">
                    {project.challenge}
                  </p>
                </div>
                <div data-id="element-166">
                  <span className="text-sm font-medium text-white/40 uppercase tracking-wider" data-id="element-167">
                    Lösung
                  </span>
                  <p className="mt-3 text-white/60 leading-relaxed" data-id="element-168">
                    {project.solution}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-10 pt-8 border-t border-white/10" data-id="element-169">
                <Link to="/kontakt" onClick={onClose} className="inline-flex items-center justify-center px-8 py-4 bg-[#E8781A] text-white font-medium rounded-lg hover:bg-[#E8781A]/90 transition-colors" data-id="element-170">
                  Ähnliches Projekt besprechen
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-171" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}